import { useEffect, useMemo, useRef, useState } from "react";
import type { PinnedNotesConfig } from "@/components/ObjectHeading";
import { useHasSearchParam } from "@/hooks/useHasSearchParam";
import type { CRMNote } from "@/types";
import { NotePriorityValues } from "@/types";
import { scrollToElement } from "@/lib/scrollHelpers";

interface UsePinnedNotesOptions {
  notes: CRMNote[];
  entityId: string;
  sessionStorageKey: string;
  openParam?: string;
}

interface UsePinnedNotesResult {
  pinnedNotes: PinnedNotesConfig | undefined;
  shouldAutoOpenNotes: boolean;
  highlightNoteId: string | null;
}

/**
 * Shared hook for pinned notes on entity detail pages (orders, payments, etc.).
 *
 * Handles filtering to promoted/alert notes, building the PinnedNotesConfig,
 * detecting alert-priority notes for auto-open, and once-per-session visit
 * tracking via sessionStorage. A `?openNote=<id>` deep link suppresses the
 * auto-open so the targeted note side sheet isn't obscured.
 */
export function usePinnedNotes({
  notes,
  entityId,
  sessionStorageKey,
  openParam = "openNote"
}: UsePinnedNotesOptions): UsePinnedNotesResult {
  const hasDeepLink = useHasSearchParam(openParam);

  const [activeHighlightNoteId, setActiveHighlightNoteId] = useState<string | null>(null);

  const filteredNotes = useMemo(() => notes.filter((n) => n.priority !== NotePriorityValues.NONE), [notes]);

  const pinnedNotes: PinnedNotesConfig | undefined = useMemo(() => {
    return {
      notes: filteredNotes,
      onViewAll: () => scrollToElement("notes-table"),
      getNoteAction: (note: CRMNote) => () => {
        setActiveHighlightNoteId(note.id);
        scrollToElement("notes-table");
      }
    };
  }, [filteredNotes]);

  const hasAlertNotes = useMemo(
    () => filteredNotes.some((n) => n.priority === NotePriorityValues.ALERT),
    [filteredNotes]
  );

  let shouldAutoOpenNotes = false;
  if (hasAlertNotes && !hasDeepLink) {
    try {
      const visited: string[] = JSON.parse(sessionStorage.getItem(sessionStorageKey) || "[]");
      shouldAutoOpenNotes = !visited.includes(entityId);
    } catch {
      // ignore sessionStorage errors
    }
  }

  const hasMarkedVisited = useRef(false);
  const prevEntityId = useRef(entityId);
  if (prevEntityId.current !== entityId) {
    prevEntityId.current = entityId;
    hasMarkedVisited.current = false;
  }
  useEffect(() => {
    if (shouldAutoOpenNotes && !hasMarkedVisited.current) {
      hasMarkedVisited.current = true;
      try {
        const visited: string[] = JSON.parse(sessionStorage.getItem(sessionStorageKey) || "[]");
        if (!visited.includes(entityId)) {
          sessionStorage.setItem(sessionStorageKey, JSON.stringify([...visited, entityId]));
        }
      } catch {
        // ignore sessionStorage errors
      }
    }
  }, [shouldAutoOpenNotes, entityId, sessionStorageKey]);

  return {
    pinnedNotes,
    shouldAutoOpenNotes,
    highlightNoteId: activeHighlightNoteId
  };
}
